/**
 * Les trois formes de graphique du tableau de bord (`ARCHITECTURE_V2.md` §6.3).
 *
 * 🔴 **Trois primitives réutilisées dix fois, ce n'est pas dix graphiques.** Le
 * §6.3 rouvre explicitement la décision de l'étape 8 (« SVG écrit à la main »)
 * puisque le §12.5 demande une dizaine de graphiques et non plus un seul, et il
 * la referme sur le même constat : les **formes** nécessaires sont peu
 * nombreuses — série temporelle, barres par semaine, nuage de points. Les voici,
 * écrites une fois.
 *
 * ⚠️ **Tout ce qui pouvait être partagé l'était déjà** : `toEpochDay`,
 * `niceTicks`, `verticalWindow`, `nearestIndex` et `tooltipAnchor` vivent dans
 * `lib/chartScale.ts` depuis l'étape 6, et `ChartTooltip` depuis le 05/09. Ce
 * fichier n'ajoute que la mise en page des trois formes.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 CE QU'AUCUNE DE CES FORMES NE TRACERA JAMAIS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Aucune droite de régression, aucun coefficient** (§6.2). Le nuage de points
 * montre les couples ; ce qu'on en tire n'appartient qu'au lecteur. Ce n'est pas
 * une fonctionnalité manquante — c'est la règle générale des statistiques,
 * héritée de l'écart kcal de la V1 (« ne corrige jamais, ne bloque jamais, ne
 * suggère jamais »).
 *
 * 🔴 **Et jamais deux axes verticaux superposés.** `WeightChart` a tranché à
 * l'étape 8 — « une seule échelle verticale ; un second axe inventerait une
 * corrélation que les données ne portent pas » — et la fiche d'exercice l'a
 * confirmé en empilant deux cadres plutôt qu'en superposant deux échelles. Deux
 * grandeurs de nature différente se lisent donc dans **deux cadres** qui
 * partagent l'axe du temps, et rien d'autre.
 *
 * ⚠️ **Mesuré en pixels réels** (`useElementWidth`), jamais dans un `viewBox`
 * étiré : ces graphiques portent des libellés d'axe, et un `viewBox` mis à
 * l'échelle les ferait passer de 10 px sur iPhone à 18 px sur la colonne du
 * bureau. C'est la leçon de `WeightChart`, qui a coûté un défaut à l'étape 8.
 */

import { useState } from 'react';

import type { StatsRange } from '../../../../shared/types';
import { Button } from '../../components/nourri';
import { ChartTooltip } from '../../components/ui/ChartTooltip';
import { useChartSweep } from '../../hooks/useChartSweep';
import { useElementWidth } from '../../hooks/useElementWidth';
import { nearestIndex, niceTicks, toEpochDay, verticalWindow } from '../../lib/chartScale';

const HAUTEUR = 150;
const MARGE = { haut: 10, droite: 8, bas: 20, gauche: 34 };

/**
 * ⚠️ **Deux points minimum pour tracer une courbe**, et l'écran dit pourquoi en
 * dessous quand il n'y en a qu'un. Règle posée à l'étape 6 avec la courbe de
 * charge : « un point seul au milieu d'un cadre vide a l'air d'un graphique
 * cassé ».
 */
export const MIN_POINTS = 2;

/** Le libellé court d'une date, pour les extrémités de l'axe du temps. */
function jourCourt(date: string): string {
  const [, mois, jour] = date.split('-');
  return `${jour}/${mois}`;
}

/**
 * ⚠️ **Deux dates seulement, aux extrémités.** Une par point chevaucherait dès
 * cinq points, c'est-à-dire au moment précis où la courbe devient intéressante.
 */
function AxeDuTemps({
  premier,
  dernier,
  largeur,
  hauteur,
}: {
  premier: string;
  dernier: string;
  largeur: number;
  hauteur: number;
}) {
  return (
    <>
      <text x={MARGE.gauche} y={hauteur - 6} className="n-chart__tick" textAnchor="start">
        {jourCourt(premier)}
      </text>
      <text x={largeur - MARGE.droite} y={hauteur - 6} className="n-chart__tick" textAnchor="end">
        {jourCourt(dernier)}
      </text>
    </>
  );
}

/* ==========================================================================
 * 1. Série temporelle
 * ======================================================================= */

export interface Trace {
  /** `null` = un TROU dans la courbe, jamais un zéro. */
  valeurs: readonly (number | null)[];
  couleur: string;
  /** Trait discontinu — l'encodage secondaire qui double la couleur. */
  tirets?: boolean;
  libelle: string;
}

/**
 * Une ou plusieurs courbes sur un axe du temps commun.
 *
 * 🔴 **Une seule échelle verticale pour tous les tracés**, donc **une seule
 * unité**. Deux grandeurs différentes demandent deux `TimeChart` empilés, pas un
 * second axe — cf. l'en-tête.
 *
 * ⚠️ **Un `null` laisse un trou**, il ne descend pas à zéro : « une courbe qui
 * plonge à zéro raconterait une régression qui n'a pas eu lieu » (étape 6). Une
 * journée non saisie et une journée à 0 kcal ne doivent pas se dessiner pareil.
 */
export function TimeChart({
  dates,
  traces,
  unite,
  formater = (v) => v.toLocaleString('fr-FR'),
}: {
  dates: readonly string[];
  traces: readonly Trace[];
  unite: string;
  formater?: (valeur: number) => string;
}) {
  const { ref, width } = useElementWidth();
  const [actif, setActif] = useState<string | null>(null);

  const toutes = traces.flatMap((t) => t.valeurs).filter((v): v is number => v !== null);
  /*
   * 🔴 **Le cas vide se décide ici mais ne SORT pas ici.** Les hooks doivent
   * être appelés à chaque rendu, or `useChartSweep` vient plus bas : un retour
   * anticipé au-dessus de lui en ferait un hook conditionnel, ce que React
   * refuse. On calcule donc tout avec des replis sûrs, et on ne branche qu'au
   * moment de rendre.
   */
  const vide = dates.length < MIN_POINTS || toutes.length === 0 || width === 0;

  // Repli sur `[0, 1]` : `verticalWindow([])` rendrait ±Infinity, donc des
  // coordonnées `NaN`. Rien de tout cela n'est peint quand `vide` est vrai.
  const { min, max } = verticalWindow(toutes.length > 0 ? toutes : [0, 1]);
  const x0 = toEpochDay(dates[0] ?? '');
  const x1 = toEpochDay(dates[dates.length - 1] ?? '');
  const etendue = Math.max(1, x1 - x0);

  const posX = (date: string) =>
    MARGE.gauche +
    ((toEpochDay(date) - x0) / etendue) * (width - MARGE.gauche - MARGE.droite);
  const posY = (valeur: number) =>
    MARGE.haut + (1 - (valeur - min) / (max - min)) * (HAUTEUR - MARGE.haut - MARGE.bas);

  const abscisses = dates.map(posX);
  /*
   * ⚠️ La sélection retient une **DATE, jamais un rang** — leçon du 05/09 :
   * « le rang 12 de 90 jours n'est pas le rang 12 de 30 jours ». Elle traverse
   * donc les changements de plage sans désigner un autre point.
   */
  const indexActif = actif === null ? -1 : dates.indexOf(actif);

  const { attacher, handlers } = useChartSweep((x, _y, auContact) => {
    const index = nearestIndex(abscisses, x);
    if (index === null) return;
    const date = dates[index];
    if (date === undefined) return;
    // Un second appui sur le même point le retire — seulement au contact.
    if (auContact && date === actif) setActif(null);
    else setActif(date);
  });

  if (vide) {
    return (
      <div ref={ref} className="n-chart-host">
        <div className="n-chart__vide">
          {dates.length < MIN_POINTS
            ? 'Il faut au moins deux jours de données pour tracer une courbe.'
            : 'Aucune valeur sur cette période.'}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="n-chart-host">
      <svg
        width={width}
        height={HAUTEUR}
        /* Le défilement vertical reste natif : sans ça, une feuille cesserait
           de défiler dès qu'on la touche par le graphique. */
        style={{ touchAction: 'pan-y' }}
        ref={attacher}
        {...handlers}
        role="img"
        aria-label={`Évolution en ${unite}`}
      >
        {niceTicks(min, max).map((tick) => (
          <g key={tick}>
            <line
              x1={MARGE.gauche}
              x2={width - MARGE.droite}
              y1={posY(tick)}
              y2={posY(tick)}
              className="n-chart__grid"
            />
            <text x={0} y={posY(tick) + 3} className="n-chart__tick">
              {formater(tick)}
            </text>
          </g>
        ))}

        {traces.map((trace) => {
          /*
           * ⚠️ Chaque suite de valeurs connues devient son **propre** segment :
           * relier par-dessus un trou dessinerait une mesure qu'on n'a pas
           * faite.
           */
          const segments: string[] = [];
          let courant: string[] = [];
          trace.valeurs.forEach((valeur, index) => {
            const date = dates[index];
            if (valeur === null || date === undefined) {
              if (courant.length > 1) segments.push(courant.join(' '));
              courant = [];
              return;
            }
            courant.push(`${String(posX(date))},${String(posY(valeur))}`);
          });
          if (courant.length > 1) segments.push(courant.join(' '));

          return segments.map((points, i) => (
            <polyline
              key={`${trace.libelle}-${String(i)}`}
              points={points}
              fill="none"
              style={{ stroke: trace.couleur }}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={trace.tirets === true ? '5 4' : undefined}
            />
          ));
        })}

        {/*
          🔴 **Le trait vertical du point désigné** *(06/09/2026, demandé après
          usage)*. Sans lui, sur trois courbes superposées, on ne sait pas
          lequel des trois ronds correspond à la valeur qu'on lit — et sur une
          courbe dense, on ne sait même pas *où* on est.

          ⚠️ **Dessiné AVANT les tracés et les points**, donc dessous : un
          repère qui passe par-dessus la mesure la masque au moment précis où
          on la regarde. C'est le même ordre que la grille.
        */}
        {indexActif >= 0 && (
          <line
            x1={posX(dates[indexActif] ?? '')}
            x2={posX(dates[indexActif] ?? '')}
            y1={MARGE.haut}
            y2={HAUTEUR - MARGE.bas}
            className="n-chart__crosshair"
          />
        )}

        {indexActif >= 0 &&
          traces.map((trace) => {
            const valeur = trace.valeurs[indexActif];
            const date = dates[indexActif];
            if (valeur === null || valeur === undefined || date === undefined) return null;
            return (
              <circle
                key={trace.libelle}
                cx={posX(date)}
                cy={posY(valeur)}
                r={4}
                /* L'anneau de surface qui détache le point du tracé. */
                style={{ fill: trace.couleur, stroke: 'var(--surface)' }}
                strokeWidth={2}
              />
            );
          })}

        <AxeDuTemps
          premier={dates[0] ?? ''}
          dernier={dates[dates.length - 1] ?? ''}
          largeur={width}
          hauteur={HAUTEUR}
        />
      </svg>

      {indexActif >= 0 && (
        <ChartTooltip
          x={posX(dates[indexActif] ?? '')}
          y={MARGE.haut}
          boxWidth={width}
          boxHeight={HAUTEUR}
        >
          <div>{dates[indexActif]}</div>
          {traces.map((trace) => {
            const valeur = trace.valeurs[indexActif];
            return (
              <div key={trace.libelle}>
                {trace.libelle} :{' '}
                {valeur === null || valeur === undefined ? '—' : `${formater(valeur)} ${unite}`}
              </div>
            );
          })}
        </ChartTooltip>
      )}

      {/*
        🔴 **Une légende dès qu'il y a deux tracés** : l'identité n'est jamais
        portée par la seule couleur. À un seul tracé, le titre de la carte le
        nomme déjà et une légende ne dirait rien de plus.
      */}
      {traces.length > 1 && (
        <div className="n-chart-legend">
          {traces.map((trace) => (
            <span key={trace.libelle}>
              <i style={{ background: trace.couleur }} />
              {trace.libelle}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 * 2. Barres par semaine
 * ======================================================================= */

export interface Barre {
  /** Lundi de la semaine — l'étiquette et l'identité du point. */
  cle: string;
  valeur: number;
  /** Ce que dit l'infobulle, au-delà de la valeur. */
  detail?: string;
}

/**
 * Des barres, une par semaine.
 *
 * 🔴 **Une seule série, jamais un empilement par muscle.** C'était la tentation
 * — le §6.2 dit « volume par groupe musculaire **et** par semaine » — et elle
 * butait sur une règle qui vaut mieux qu'elle : au-delà de huit catégories, une
 * couleur de plus n'est plus une identité, c'est du bruit. Or il y a
 * **dix-sept** muscles. Empiler aurait demandé d'inventer dix-sept teintes
 * catégorielles, alors que la palette du projet en compte quatre — et que la
 * mesure (cf. le journal de bord) montre que ces quatre-là ne se distinguent
 * déjà pas assez pour porter une identité **seules**.
 *
 * D'où le découpage retenu : cette forme dit **quand**, à muscle constant ou sur
 * le total ; la répartition par muscle se lit dans une liste de barres
 * horizontales, qui n'a besoin d'aucune couleur pour être lue. Deux questions,
 * deux formes, zéro teinte inventée.
 *
 * ⚠️ **Un espace de 2 px entre les barres**, pris sur la barre et non ajouté
 * autour : c'est ce qui les sépare sans décaler l'axe.
 */
export function WeekBars({
  barres,
  unite,
  couleur = 'var(--brand)',
}: {
  barres: readonly Barre[];
  unite: string;
  couleur?: string;
}) {
  const { ref, width } = useElementWidth();
  const [actif, setActif] = useState<string | null>(null);

  // Cf. `TimeChart` : on décide du cas vide sans sortir avant les hooks.
  const vide = barres.length === 0 || width === 0;

  const max = Math.max(...barres.map((b) => b.valeur), 1);
  const zone = width - MARGE.gauche - MARGE.droite;
  const pas = zone / barres.length;
  /*
   * ⚠️ **Un plancher de 1 px de large.** Sur cinq ans, 260 semaines se
   * partagent ~340 px : sans plancher, `Math.floor` en ramènerait certaines à
   * zéro et des semaines travaillées disparaîtraient de l'écran.
   */
  const largeurBarre = Math.max(1, pas - 2);

  const indexActif = actif === null ? -1 : barres.findIndex((b) => b.cle === actif);
  const barreActive = indexActif >= 0 ? barres[indexActif] : undefined;

  const { attacher, handlers } = useChartSweep((x, _y, auContact) => {
    const index = nearestIndex(
      barres.map((_, i) => MARGE.gauche + i * pas + pas / 2),
      x,
    );
    if (index === null) return;
    const cle = barres[index]?.cle;
    if (cle === undefined) return;
    if (auContact && cle === actif) setActif(null);
    else setActif(cle);
  });

  if (vide) {
    return (
      <div ref={ref} className="n-chart-host">
        <div className="n-chart__vide">Aucune semaine sur cette période.</div>
      </div>
    );
  }

  return (
    <div ref={ref} className="n-chart-host">
      <svg
        width={width}
        height={HAUTEUR}
        style={{ touchAction: 'pan-y' }}
        ref={attacher}
        {...handlers}
        role="img"
        aria-label={`Par semaine, en ${unite}`}
      >
        {niceTicks(0, max).map((tick) => (
          <g key={tick}>
            <line
              x1={MARGE.gauche}
              x2={width - MARGE.droite}
              y1={MARGE.haut + (1 - tick / max) * (HAUTEUR - MARGE.haut - MARGE.bas)}
              y2={MARGE.haut + (1 - tick / max) * (HAUTEUR - MARGE.haut - MARGE.bas)}
              className="n-chart__grid"
            />
            <text
              x={0}
              y={MARGE.haut + (1 - tick / max) * (HAUTEUR - MARGE.haut - MARGE.bas) + 3}
              className="n-chart__tick"
            >
              {tick.toLocaleString('fr-FR')}
            </text>
          </g>
        ))}

        {/*
          ⚠️ **Le trait du point désigné, sous les barres.** Sur des barres, il
          fait moins que sur une courbe — la barre pleine se repère déjà — mais
          il aligne la lecture avec les autres graphiques, et il sert quand les
          barres font 1 px de large (260 semaines sur la plage « Tout »).
        */}
        {indexActif >= 0 && (
          <line
            x1={MARGE.gauche + indexActif * pas + pas / 2}
            x2={MARGE.gauche + indexActif * pas + pas / 2}
            y1={MARGE.haut}
            y2={HAUTEUR - MARGE.bas}
            className="n-chart__crosshair"
          />
        )}

        {barres.map((barre, index) => {
          const hauteur = (barre.valeur / max) * (HAUTEUR - MARGE.haut - MARGE.bas);
          return (
            <rect
              key={barre.cle}
              x={MARGE.gauche + index * pas}
              y={HAUTEUR - MARGE.bas - hauteur}
              width={largeurBarre}
              height={Math.max(0, hauteur)}
              rx={largeurBarre >= 8 ? 3 : 0}
              style={{ fill: couleur }}
              opacity={indexActif < 0 || index === indexActif ? 1 : 0.45}
            />
          );
        })}

        <AxeDuTemps
          premier={barres[0]?.cle ?? ''}
          dernier={barres[barres.length - 1]?.cle ?? ''}
          largeur={width}
          hauteur={HAUTEUR}
        />
      </svg>

      {barreActive !== undefined && (
        <ChartTooltip
          x={MARGE.gauche + indexActif * pas + pas / 2}
          y={MARGE.haut}
          boxWidth={width}
          boxHeight={HAUTEUR}
        >
          <div>Semaine du {jourCourt(barreActive.cle)}</div>
          <div>
            {barreActive.valeur.toLocaleString('fr-FR')} {unite}
          </div>
          {barreActive.detail !== undefined && <div>{barreActive.detail}</div>}
        </ChartTooltip>
      )}
    </div>
  );
}

/* ==========================================================================
 * 3. Nuage de points
 * ======================================================================= */

/**
 * Un nuage de points — **et rien de plus**.
 *
 * 🔴 **Aucune droite de régression, aucun coefficient de corrélation** (§6.2).
 * C'est la forme où la tentation est la plus forte, précisément parce qu'on
 * aimerait qu'elle dise quelque chose : le §6.2 a écarté la corrélation apport ↔
 * poids pour deux raisons qui valent ici — sur un sujet unique et sans période
 * de contrôle, une corrélation entre deux séries tendancielles est fallacieuse
 * presque par construction, et elle serait lue comme causale.
 *
 * ⚠️ **Les deux axes portent leur unité**, sans quoi un nuage ne se lit pas :
 * contrairement à une série temporelle, aucun des deux axes n'est deviné.
 */
export function Scatter({
  points,
  legendeX,
  legendeY,
  formaterX = (v) => v.toLocaleString('fr-FR'),
  formaterY = (v) => v.toLocaleString('fr-FR'),
}: {
  points: readonly { date: string; x: number; y: number }[];
  legendeX: string;
  legendeY: string;
  formaterX?: (v: number) => string;
  formaterY?: (v: number) => string;
}) {
  const { ref, width } = useElementWidth();
  const [actif, setActif] = useState<string | null>(null);

  // Cf. `TimeChart` : on décide du cas vide sans sortir avant les hooks.
  const vide = points.length < MIN_POINTS || width === 0;

  const fx = verticalWindow(points.length > 0 ? points.map((p) => p.x) : [0, 1]);
  const fy = verticalWindow(points.length > 0 ? points.map((p) => p.y) : [0, 1]);
  const posX = (v: number) =>
    MARGE.gauche + ((v - fx.min) / (fx.max - fx.min)) * (width - MARGE.gauche - MARGE.droite);
  const posY = (v: number) =>
    MARGE.haut + (1 - (v - fy.min) / (fy.max - fy.min)) * (HAUTEUR - MARGE.haut - MARGE.bas);

  const pointActif = points.find((p) => p.date === actif);

  const { attacher, handlers } = useChartSweep((cx, cy, auContact) => {
    /*
     * ⚠️ Le plus proche **en deux dimensions** : sur un nuage, désigner par la
     * seule abscisse attraperait un point situé à l'autre bout de l'écran
     * verticalement.
     */
    let meilleur: string | null = null;
    let distance = Number.POSITIVE_INFINITY;
    for (const point of points) {
      const d = (posX(point.x) - cx) ** 2 + (posY(point.y) - cy) ** 2;
      if (d < distance) {
        distance = d;
        meilleur = point.date;
      }
    }
    if (auContact && meilleur === actif) setActif(null);
    else setActif(meilleur);
  });

  if (vide) {
    return (
      <div ref={ref} className="n-chart-host">
        <div className="n-chart__vide">
          Il faut au moins deux séances où les deux mesures existent.
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="n-chart-host">
      <svg
        width={width}
        height={HAUTEUR + 14}
        style={{ touchAction: 'pan-y' }}
        ref={attacher}
        {...handlers}
        role="img"
        aria-label={`${legendeY} selon ${legendeX}`}
      >
        {niceTicks(fy.min, fy.max).map((tick) => (
          <g key={tick}>
            <line
              x1={MARGE.gauche}
              x2={width - MARGE.droite}
              y1={posY(tick)}
              y2={posY(tick)}
              className="n-chart__grid"
            />
            <text x={0} y={posY(tick) + 3} className="n-chart__tick">
              {formaterY(tick)}
            </text>
          </g>
        ))}

        {/*
          ⚠️ **Sur un nuage, le repère est une CROIX** — les deux axes portent
          une grandeur, donc un trait vertical seul ne situerait le point que
          sur la moitié de ce qui le définit.
        */}
        {pointActif !== undefined && (
          <>
            <line
              x1={posX(pointActif.x)}
              x2={posX(pointActif.x)}
              y1={MARGE.haut}
              y2={HAUTEUR - MARGE.bas}
              className="n-chart__crosshair"
            />
            <line
              x1={MARGE.gauche}
              x2={width - MARGE.droite}
              y1={posY(pointActif.y)}
              y2={posY(pointActif.y)}
              className="n-chart__crosshair"
            />
          </>
        )}

        {points.map((point) => (
          <circle
            key={point.date}
            cx={posX(point.x)}
            cy={posY(point.y)}
            r={point.date === actif ? 6 : 4}
            style={{ fill: 'var(--brand)', stroke: 'var(--surface)' }}
            strokeWidth={2}
            opacity={actif === null || point.date === actif ? 0.9 : 0.4}
          />
        ))}

        <text x={MARGE.gauche} y={HAUTEUR + 8} className="n-chart__tick" textAnchor="start">
          {formaterX(fx.min)}
        </text>
        <text
          x={width - MARGE.droite}
          y={HAUTEUR + 8}
          className="n-chart__tick"
          textAnchor="end"
        >
          {formaterX(fx.max)} {legendeX}
        </text>
      </svg>

      {pointActif !== undefined && (
        <ChartTooltip x={posX(pointActif.x)} y={posY(pointActif.y)} boxWidth={width} boxHeight={HAUTEUR}>
          <div>{pointActif.date}</div>
          <div>
            {legendeX} : {formaterX(pointActif.x)}
          </div>
          <div>
            {legendeY} : {formaterY(pointActif.y)}
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}

/* ==========================================================================
 * Barres horizontales — la répartition, sans axe du temps
 * ======================================================================= */

/**
 * Une répartition par catégorie, en barres horizontales.
 *
 * 🔴 **C'est la forme qui remplace l'empilement par muscle**, et elle est
 * meilleure pour la question posée : « où va le travail ? » se lit dans un
 * classement, pas dans une pile de dix-sept segments hauts de trois pixels. Les
 * libellés sont **écrits**, donc l'identité ne dépend d'aucune couleur — ce qui
 * règle d'un coup le problème que dix-sept teintes catégorielles auraient posé.
 *
 * ⚠️ **La valeur est écrite au bout de chaque barre.** Une barre sans nombre
 * demande de viser une graduation ; avec le nombre, la barre ne sert plus qu'à
 * comparer d'un coup d'œil, ce qu'elle fait bien.
 */
export function RankedBars({
  lignes,
  unite,
  onSelect,
  selection,
}: {
  lignes: readonly { cle: string; libelle: string; valeur: number; detail?: string }[];
  unite: string;
  onSelect?: (cle: string) => void;
  selection?: string | null;
}) {
  if (lignes.length === 0) {
    return <div className="n-chart__vide">Rien à répartir sur cette période.</div>;
  }
  const max = Math.max(...lignes.map((l) => l.valeur), 1);

  return (
    <ul className="n-ranked">
      {lignes.map((ligne) => {
        const actif = selection === ligne.cle;
        const contenu = (
          <>
            <span className="n-ranked__lbl">{ligne.libelle}</span>
            <span className="n-ranked__bar">
              <i style={{ width: `${String(Math.max(2, (ligne.valeur / max) * 100))}%` }} />
            </span>
            <span className="n-ranked__val tnum">
              {ligne.valeur.toLocaleString('fr-FR')}
              <span className="n-ranked__unit"> {unite}</span>
            </span>
          </>
        );
        return (
          <li key={ligne.cle} className={actif ? 'is-active' : undefined}>
            {onSelect === undefined ? (
              <div className="n-ranked__row">{contenu}</div>
            ) : (
              <button
                type="button"
                className="n-ranked__row"
                aria-pressed={actif}
                onClick={() => {
                  onSelect(ligne.cle);
                }}
              >
                {contenu}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ==========================================================================
 * La rangée de plages
 * ======================================================================= */

const RANGES: readonly { value: StatsRange; label: string }[] = [
  { value: '30d', label: '30 j' },
  { value: '90d', label: '90 j' },
  { value: '1y', label: '1 an' },
  { value: 'all', label: 'Tout' },
];

/**
 * Les quatre plages, au-dessus de ce qu'elles gouvernent.
 *
 * ⚠️ **Les mêmes quatre que l'écran de poids**, et sous les mêmes libellés :
 * les deux vivent sur le même onglet, et deux vocabulaires de période y
 * auraient produit « 90 j » d'un côté et « 3 mois » de l'autre.
 *
 * ⚠️ **Chaque section porte la sienne**, plutôt qu'une seule pour tout
 * l'onglet. C'est déjà le parti pris de l'écran de poids depuis l'étape 8, et
 * il évite le défaut inverse : une plage commune ferait recalculer cinq ans de
 * musculation parce qu'on a élargi la fenêtre de nutrition.
 */
export function RangeRow({
  range,
  onChange,
}: {
  range: StatsRange;
  onChange: (range: StatsRange) => void;
}) {
  return (
    <div className="n-period-row" style={{ marginBottom: 14 }}>
      {RANGES.map((option) => {
        const actif = option.value === range;
        return (
          <Button
            key={option.value}
            variant={actif ? 'selected' : 'secondary'}
            aria-pressed={actif}
            onClick={() => {
              onChange(option.value);
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
